import { useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import pagarme from "pagarme/browser";

import traceId from "../../../redux/traceId";
import axios from "axios";
import useFeatureFlag from "../../hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "../../constants/FeatureFlag";
import { tracking } from "../../services/tracking";
import {
  trackCheckoutCardTokenizationError,
  trackCheckoutCardTokenizationSuccess,
  trackCheckoutPaymentFailed,
  trackCheckoutPixPaymentClicked,
  trackCheckoutPixPaymentFailed,
  trackCheckoutTryAgainClicked,
  trackCheckoutAccessPlatformClicked,
  trackSignUpAccountCreated,
  trackSignUpAccountCreationFailed,
} from "app/services/tracking/payments/checkoutStepsEvents";
import { trackSignUpAccountCreationSubmit } from "app/services/tracking/payments/cadastroEvents";
import {
  createCardSubscription,
  createPagarmeToken,
  createPixSubscription,
  fetchSubscription,
  getClientIp,
  persistClient,
  provisionCapicandia,
  saveCardOnBackend,
  signupInfoprodutores,
} from "./api";
import {
  buildCheckoutValues,
  getCheckoutVersion,
} from "../../services/checkout/checkoutMode";
import { buildTrackingUrlParams } from "../../services/checkout/trackingParams";
import {
  isCapicandiaCheckoutCoupon,
  getCapicandiaVariant,
  shouldProvisionCapicandia,
} from "./validation";
import {
  CHECKOUT_PROVIDER,
  resolveCheckoutProvider,
} from "./useCheckoutProviderMode";
import { resolveCoupomForCheckout } from "../Cadastro/plans";

export { resolveCoupomForCheckout };

// A Jussub plan never carries `tier`/`gateway_id`, so matching
// it against the catalog offers below never finds a coupon and resolveCoupomForCheckout degrades
// to a catalog coupon with plan_id undefined, which combined with the plan's missing gateway_id
// makes resolveCheckoutPlanId throw "Plano sem gateway_id". Guard the catalog coupon lookup off
// whenever the plan came from Jussub.
export const resolveCoupomForOffer = ({
  plan,
  pricingCatalogOffers,
  isJussubEnabled,
  coupom,
}) => {
  if (isJussubEnabled) return coupom;
  const catalogCoupon = (pricingCatalogOffers || []).find(
    offer => offer.tier === plan?.id
  )?.coupon;
  return resolveCoupomForCheckout({ coupom, catalogCoupon });
};

function getCardBrand(cardNumber) {
  const number = cardNumber.replace(/\D/g, "");
  const bin = number.slice(0, 6);
  if (/^4/.test(bin)) return "visa";
  if (/^5[1-5]/.test(bin)) return "mastercard";
  if (/^3[47]/.test(bin)) return "amex";
  if (/^3(?:0[0-5]|[68])/.test(bin)) return "diners";
  if (/^6(?:011|5)/.test(bin)) return "discover";
  if (/^35(2[89]|[3-8][0-9])/.test(bin)) return "jcb";
  if (/^(50|60|65|4011|4312|4389|4514|4576|5041|5066|5067|509|6277|6362|6363)/.test(bin)) return "elo";
  if (/^606282|3841/.test(bin)) return "hipercard";
  if (/^637095|637568|637599|637609|637612/.test(bin)) return "banescard";
  return "unknown";
}

function maskCardNumber(cardNumber) {
  const raw = (cardNumber || "").replace(/\s/g, "");
  if (!raw) return "";
  return `${raw.slice(0, 6)}***${raw.slice(-4)}`;
}

function captureError(scopeData) {
  Sentry.withScope(scope => {
    scope.setTag("area", "cadastro_v2");
    Object.entries(scopeData.tags || {}).forEach(([k, v]) => scope.setTag(k, v));
    Object.entries(scopeData.extras || {}).forEach(([k, v]) => scope.setExtra(k, v));
    const sanitized = new Error(scopeData.message);
    sanitized.name = scopeData.name || "Error";
    Sentry.captureException(sanitized);
  });
}

// Mensagens do ramo infoprodutor. CARD_SAVE_ERROR_MESSAGE é a mesma string que
// saveCard já lançava; virar constante permite distinguir "cartão recusado" de
// erro genérico no catch de submitInfoprodutores sem mudar o fluxo Jusfy.
const CARD_SAVE_ERROR_MESSAGE = "Erro ao registrar o cartão";
const CARD_REJECTED_MESSAGE =
  "Cartão recusado. Confira os dados do cartão e tente novamente.";
const SIGNUP_GENERIC_MESSAGE =
  "Não foi possível concluir seu cadastro. Tente novamente em instantes.";

const isSubscriptionActive = subscription =>
  subscription?.status === "active" || subscription?.status === "paid";

export const buildJussubCheckoutPayload = ({
  values,
  token,
  paymentMethod,
  isPasswordlessCheckout,
}) => {
  const rawPhone = (values.phone || "").replace(/\D/g, "");
  const cardRaw = (values.card_number || "").replace(/\s/g, "");
  const [expMonth, expYear] = (values.card_expiration || "/").split("/");

  const payload = {
    paymentMethod,
    version: getCheckoutVersion(isPasswordlessCheckout),
    requiresFirstAccess: !!isPasswordlessCheckout,
    requires_first_access: !!isPasswordlessCheckout,
    customer: {
      email: values.email,
      name: values.name,
      documentNumber: (values.document_number || "").replace(/\D/g, ""),
      ...(!isPasswordlessCheckout ? { password: values.password } : {}),
      address: {
        street: values.address,
        streetNumber: values.address_number,
        neighborhood: values.district,
        zipcode: (values.postal_code || "").replace(/\D/g, ""),
        city: values.city?.name,
        state: values.state?.state,
        complement: values.address_complement || "",
        country: "BR",
      },
      phone: {
        countryCode: "55",
        ddd: rawPhone.slice(0, 2),
        number: rawPhone.slice(2),
      },
    },
    items: [{ itemId: values.plan.id, itemType: "PLAN" }],
  };

  if (values.coupom) {
    payload.coupons = [{ code: values.coupom }];
  }

  if (paymentMethod === "CREDIT_CARD" && token) {
    payload.cards = [
      {
        gatewayTokens: [{ gateway: "PAGARME", token }],
        lastFour: cardRaw.slice(-4),
        brand: getCardBrand(cardRaw),
        expMonth,
        expYear,
        holderName: values.cardholder_name,
      },
    ];
  }

// "token_34dqDoESxhyjXOZN"

  return payload;
};

export default function useCheckoutFlow({
  formik,
  locationSearch,
  setSubmitting,
  setResult,
  setFailedReason,
  setPixPaymentData,
  setPixGenerationFailed,
  setDisableButton,
  setEnableRecaptchaV2,
  formErrors,
  isPasswordlessCheckout,
  isJussubEnabled,
  isJussubResolved,
  pricingCatalogOffers,
  isInfoprodutoresMode = false,
  infoprodutoresCoupon = null,
}) {
  const coupom = useSelector(state => state.app.coupom);
  const defaultTrialDays = useSelector(state => state.app.default_trial_days);

  const resolveCoupomForPlan = plan =>
    resolveCoupomForOffer({
      plan,
      pricingCatalogOffers,
      isJussubEnabled,
      coupom,
    });
  const {
    isFeatureFlagEnable: enableAddCardSentry,
    flagsReady,
  } = useFeatureFlag(FEATURE_FLAGS.KILL_SWITCH.ENABLE_ADD_CARD_SENTRY);
  const sentryEnabled = enableAddCardSentry && flagsReady;
  const refreshRecaptchaV3Ref = useRef();

  const trackPaymentFailed = (values, errorPayload) => {
    const props = tracking.checkout.preparePaymentFailedForAnalytics(
      values,
      errorPayload,
      coupom,
      defaultTrialDays
    );
    trackCheckoutPaymentFailed(props);
  };

  const tokenize = async values => {
    try {
      const data = await createPagarmeToken(values);
      const tokenData = { id: data.id, card_hash: data.id };
      const props = tracking.checkout.prepareCardTokenizationSuccessForAnalytics(
        values, tokenData, coupom, defaultTrialDays
      );
      trackCheckoutCardTokenizationSuccess(props);
      return data;
    } catch (error) {
      const isCors = !error.response &&
        (error.message?.includes("CORS") || error.code === "ERR_NETWORK" || error.name === "NetworkError");
      const props = tracking.checkout.prepareCheckoutCardTokenizationErrorForAnalytics(
        values,
        {
          error_type: isCors ? "cors_error" : "pagarme_v5_tokenization_error",
          error_message:
            error.response?.data?.message ||
            error.response?.data?.msg ||
            error.message ||
            "Erro ao criar o token do cartão",
          error_code: isCors ? "CORS_ERROR" : (error.response?.status || "unknown"),
        },
        coupom,
        defaultTrialDays
      );
      trackCheckoutCardTokenizationError(props);
      if (sentryEnabled) {
        captureError({
          tags: { flow: "create_token", provider: "pagarme_v5", trace_id: traceId },
          extras: {
            endpoint: "//api.pagar.me/core/v5/tokens",
            status: error?.response?.status || "unknown",
            errors_history: formErrors.slice(-50),
          },
          message: error?.message || "Card tokenization failed",
          name: error?.name || "AxiosError",
        });
      }
      toast.error("Falha ao prosseguir com o pagamento, por favor, tente novamente.");
      setSubmitting(false);
      setDisableButton(false);
      throw new Error("Erro ao criar o token do cartão");
    }
  };

  const saveCard = async (token, values) => {
    try {
      const ip = await getClientIp();
      return await saveCardOnBackend({ token, values, ip, getCardBrand });
    } catch (error) {
      trackPaymentFailed(values, {
        error_type: "card_save_error",
        error_message:
          error.response?.data?.message ||
          error.response?.data?.msg ||
          error.message ||
          "Erro ao registrar o cartão",
        error_code: error.response?.status || "unknown",
      });
      if (sentryEnabled) {
        captureError({
          tags: { flow: "save_card_to_backend", provider: "pagarme_v5", trace_id: traceId },
          extras: {
            endpoint: "/client-cards/pagarme-v5",
            status: error?.response?.status || "unknown",
            errors_history: formErrors.slice(-50),
            card_brand: getCardBrand(values.card_number.replace(/\s/g, "")),
            card_number: maskCardNumber(values.card_number),
          },
          message: error?.message || "Card save failed",
          name: error?.name || "AxiosError",
        });
      }
      setSubmitting(false);
      setDisableButton(false);
      throw new Error(CARD_SAVE_ERROR_MESSAGE);
    }
  };

  const handleInvalidRecaptcha = error => {
    toast.error(
      error?.response?.data?.message ||
        "Erro de reCaptcha, por favor tente novamente."
    );
    setEnableRecaptchaV2?.(true);
    setSubmitting(false);
    setDisableButton(true);
    setResult("idle");
    const tagged = new Error("INVALID_RECAPTCHA");
    tagged.code = "INVALID_RECAPTCHA";
    return tagged;
  };

  const subscribe = async (values, cardData) => {
    try {
      return await createCardSubscription({
        values,
        coupom: resolveCoupomForPlan(values.plan),
        cardData,
        isPasswordlessCheckout,
      });
    } catch (error) {
      if (error?.response?.data?.error === "INVALID_RECAPTCHA") {
        throw handleInvalidRecaptcha(error);
      }
      console.error("Subscription creation failed:", {
        status: error?.response?.status,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.message,
        endpoint: isPasswordlessCheckout ? "/subscriptions/v2" : "/subscriptions",
        trace_id: traceId,
      });
      trackPaymentFailed(values, {
        error_type: "subscription_creation_error",
        error_message:
          error.response?.data?.message ||
          error.response?.data?.msg ||
          error.message ||
          "Erro ao criar a assinatura",
        error_code: error.response?.status || "unknown",
      });
      if (sentryEnabled) {
        captureError({
          tags: { flow: "create_subscription", provider: "pagarme_v5", trace_id: traceId },
          extras: {
            endpoint: isPasswordlessCheckout
              ? "/subscriptions/v2"
              : "/subscriptions",
            status: error?.response?.status || "unknown",
            errors_history: formErrors.slice(-50),
          },
          message: error?.message || "Subscription creation failed",
          name: error?.name || "AxiosError",
        });
      }
      setSubmitting(false);
      setDisableButton(false);
      throw new Error("Erro ao criar a assinatura");
    }
  };

  const pollSubscription = async subscriptionId => {
    const MAX = 300 * 1000;
    const start = Date.now();
    const interval = secs => (secs <= 60 ? 5000 : secs <= 120 ? 10000 : 20000);
    while (Date.now() - start < MAX) {
      try {
        const resp = await fetchSubscription(subscriptionId);
        if (resp.data.reason) {
          setFailedReason(resp.data.reason);
          trackPaymentFailed(formik.values, resp.data.reason);
        }
        if (isSubscriptionActive(resp.data)) return true;
        if (resp.data.status === "failed") {
          setResult("error");
          setSubmitting(false);
          return false;
        }
        const elapsed = Math.floor((Date.now() - start) / 1000);
        await new Promise(r => setTimeout(r, interval(elapsed)));
      } catch (error) {
        if (sentryEnabled) {
          captureError({
            tags: { flow: "polling_subscription", provider: "pagarme_v5", trace_id: traceId },
            extras: {
              endpoint: "/subscriptions/id",
              status: error?.response?.status || "unknown",
              errors_history: formErrors.slice(-50),
            },
            message: error?.message || "Subscription polling failed",
            name: error?.name || "AxiosError",
          });
        }
        trackPaymentFailed(formik.values, {
          error_type: "polling_error",
          error_message: error.message || "Erro no polling da assinatura",
          error_code: error.response?.status || "unknown",
        });
        return false;
      }
    }
    setResult("error");
    setSubmitting(false);
    return false;
  };

  const registerCapicandiaWithRetry = async ({ values, subscriptionId }) => {
    const code = values.coupom;
    if (!isCapicandiaCheckoutCoupon(code)) return;
    if (!shouldProvisionCapicandia(code)) return;
    const planVariant = getCapicandiaVariant(code);

    const MAX_ATTEMPTS = 3;
    let lastError;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        await provisionCapicandia({ values, subscriptionId, planVariant });
        return;
      } catch (error) {
        lastError = error;
        const status = error?.response?.status;
        if (status >= 400 && status < 500) break;
        if (attempt < MAX_ATTEMPTS) {
          await new Promise(r => setTimeout(r, 500 * attempt));
        }
      }
    }
    if (sentryEnabled) {
      captureError({
        tags: { flow: "provision_capicandia", trace_id: traceId },
        extras: {
          endpoint: "/api/capicandia/provision",
          subscription_id: subscriptionId,
          plan_variant: planVariant,
          status: lastError?.response?.status || "unknown",
          error_code: lastError?.response?.data?.code || "unknown",
          backend_trace_id: lastError?.response?.data?.traceId || "unknown",
        },
        message:
          lastError?.response?.data?.message ||
          lastError?.message ||
          "Capicandia provisioning failed",
        name: lastError?.name || "Error",
      });
    }
  };

  // UMA requisição, sem retry — diferente do registerCapicandiaWithRetry. Aqui o
  // signup É o cadastro: cada tentativa abre um addon_checkout novo no
  // addon-manager, e um 5xx pode vir DEPOIS de o aluno já existir no tenant
  // (aconteceu em 2026-08-28: retry criou 2º checkout e recebeu 409). Quando o
  // service do infoprodutor está fora, o próprio addon-manager enfileira (202).
  // O erro é relançado para o chamador decidir a UX.
  const signupInfoprodutoresOnce = async ({ values, createCard, coupon }) => {
    try {
      return await signupInfoprodutores({ values, createCard, coupon });
    } catch (error) {
      if (sentryEnabled) {
        captureError({
          tags: { flow: "signup_infoprodutores", trace_id: traceId },
          extras: {
            endpoint: "/api/infoprodutores/signup",
            plan_variant: coupon?.plan_variant,
            jusbill_card_id: createCard?.data?.id,
            status: error?.response?.status || "unknown",
            error_code: error?.response?.data?.error || "unknown",
            backend_trace_id: error?.response?.data?.traceId || "unknown",
          },
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Infoprodutores signup failed",
          name: error?.name || "Error",
        });
      }
      throw error;
    }
  };

  // Ramo infoprodutor (spec checkout-jusfy-cupom-trial §3 passo 4): captura o
  // cartão pelo caminho Jusbill já existente e manda o cadastro ao addon-manager.
  // Não cria assinatura Jusfy (D5), não faz polling, não dispara conversão de
  // marketing (plan.amount da Jusfy não vale aqui).
  const submitInfoprodutores = async values => {
    const coupon = infoprodutoresCoupon;
    try {
      setSubmitting(true);
      setResult("idle");

      let cardToken;
      try {
        cardToken = await tokenize(values);
      } catch (_) {
        // tokenize já mostrou o toast, trackeou e liberou o botão
        return false;
      }

      // Garante o client Jusbill: o efeito de lead em index.js só persiste
      // quando a flag ENABLE_CHECKOUT_CUSTOMER_CREATE está ligada e o usuário
      // preencheu tudo antes de clicar. Aqui não pode depender disso.
      let createdCustomerId = values.createdCustomerId;
      if (!createdCustomerId) {
        const persisted = await persistClient({
          name: values.name,
          email: values.email,
        });
        createdCustomerId = persisted?.data?.id || "";
        if (createdCustomerId) {
          formik.setFieldValue("createdCustomerId", createdCustomerId);
        }
      }

      const createCard = await saveCard(cardToken.id, {
        ...values,
        createdCustomerId,
      });

      // Jusbill pode responder HTTP 200 com {statusCode:422} no body (IP
      // ausente / CEP bloqueado) — sem id/token não há cartão salvo (spec §7).
      if (createCard?.data?.statusCode === 422 || !createCard?.data?.id) {
        trackPaymentFailed(values, {
          error_type: "card_save_rejected",
          error_message: createCard?.data?.message || "Cartão recusado",
          error_code: createCard?.data?.statusCode || "unknown",
        });
        toast.error(CARD_REJECTED_MESSAGE);
        setDisableButton(false);
        return false;
      }

      if (values.recaptchaVersion === "3") {
        refreshRecaptchaV3Ref.current?.();
      }

      const response = await signupInfoprodutoresOnce({
        values,
        createCard,
        coupon,
      });

      // 201 provisionado; 202 enfileirado no addon-manager (service fora) —
      // para o aluno os dois são "conta criada" (spec §7).
      setResult("infoprodutores-success");
      trackSignUpAccountCreated({
        plan_variant: coupon?.plan_variant,
        checkout_id: response?.data?.checkout_id || null,
        queued: response?.data?.queued === true,
      });
      return true;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        toast.error(`Você já tem conta. Entre em ${coupon?.app_url}/login`);
      } else if (error?.message === CARD_SAVE_ERROR_MESSAGE) {
        // saveCard já trackeou e reportou; falta só avisar o usuário
        toast.error(CARD_REJECTED_MESSAGE);
      } else {
        trackPaymentFailed(values, {
          error_type: "infoprodutores_signup_error",
          error_message:
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Erro ao concluir o cadastro",
          error_code: status || "unknown",
        });
        toast.error(SIGNUP_GENERIC_MESSAGE);
      }
      // Não usa ModalFailedPayment: ele oferece Pix, que não existe neste fluxo.
      setResult("idle");
      setDisableButton(false);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckoutMarketingData = () => {
    if (process.env.NODE_ENV !== "production") return;
    const plan = formik.values.plan;
    let amountConversion = 47.0;
    if (plan?.amount) {
      amountConversion = parseFloat(plan.amount.replace(".", "").replace(",", "."));
    }
    window.gtag?.("event", "conversion", {
      send_to: "AW-380889985/PZWiCIe1-soDEIHXz7UB",
      value: amountConversion,
      currency: "BRL",
      transaction_id: "",
    });
  };

  const validateCardWithProvider = values => {
    const card = {
      card_holder_name: values.cardholder_name,
      card_expiration_date:
        values.card_expiration &&
        values.card_expiration.substr(0, 2) +
          "/" +
          values.card_expiration.substr(values.card_expiration.length - 2),
      card_number: values.card_number,
      card_cvv: values.card_cvv,
    };
    if (!card.card_holder_name && !card.card_expiration_date && !card.card_number && !card.card_cvv) {
      return true;
    }
    const v = pagarme.validate({ card }).card;
    const checks = [
      ["card_holder_name", "Verifique o nome impresso no cartão."],
      ["card_number", "Verifique o número do cartão."],
      ["card_expiration_date", "Verifique a data de expiração do cartão."],
      ["card_cvv", "Verifique o código de segurança (CVV) do cartão."],
    ];
    for (const [field, msg] of checks) {
      if (!v[field]) {
        trackSignUpAccountCreationFailed({
          error: "PROVIDER_CREDIT_CARD_CHECK_FAILED",
          message: msg,
        });
        toast.error(msg);
        setSubmitting(false);
        setDisableButton(false);
        return false;
      }
    }
    return true;
  };

  const submitJussub = async (values, paymentMethod = "CREDIT_CARD") => {
    let token = null;
    if (paymentMethod === "CREDIT_CARD") {
      const cardToken = await tokenize(values);
      token = cardToken.id;
    }

    const payload = buildJussubCheckoutPayload({
      values,
      token,
      paymentMethod,
      isPasswordlessCheckout,
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_JUSSUB_URL}/v1/checkout`,
        payload,
        { headers: { "x-trace-id": traceId } },
      );

      if (!response.data?.subscriptionId) {
        throw new Error("Checkout sem subscriptionId");
      }

      return response.data;
    } catch (error) {
      trackPaymentFailed(values, {
        error_type: "jussub_checkout_error",
        error_message:
          error.response?.data?.message ||
          error.response?.data?.msg ||
          error.message ||
          "Erro ao processar o checkout",
        error_code: error.response?.status || "unknown",
      });
      if (sentryEnabled) {
        captureError({
          tags: { flow: "jussub_checkout", provider: "jussub", trace_id: traceId },
          extras: {
            endpoint: "/v1/checkout",
            status: error?.response?.status || "unknown",
            errors_history: formErrors.slice(-50),
          },
          message: error?.message || "Jussub checkout failed",
          name: error?.name || "AxiosError",
        });
      }
      setSubmitting(false);
      setDisableButton(false);
      throw error;
    }
  };

  const onSubmit = async values => {
    // O modo infoprodutor não passa pelo provider Jussub×Jusbill: o cartão vai
    // direto ao Jusbill e o cadastro ao addon-manager. Por isso a resolução do
    // provider — que lança CHECKOUT_PROVIDER_PENDING enquanto a flag não chega —
    // só roda no fluxo Jusfy (spec §4.2, "indiferente à flag").
    let checkoutProvider = null;
    if (!isInfoprodutoresMode) {
      try {
        checkoutProvider = resolveCheckoutProvider({
          isResolved: isJussubResolved,
          isJussubEnabled,
          paymentMethod: "CREDIT_CARD",
        });
      } catch (error) {
        Sentry.captureException(error);
        return false;
      }
    }

    Sentry.setExtra("errors_history", formErrors);
    trackSignUpAccountCreationSubmit({
      recaptcha_version: values?.recaptchaVersion,
      has_recaptcha_token: !!values?.recaptchaToken,
    });
    setDisableButton(true);
    if (!validateCardWithProvider(values)) return false;

    if (isInfoprodutoresMode) return submitInfoprodutores(values);

    try {
      setSubmitting(true);
      setResult("idle");

      if (checkoutProvider === CHECKOUT_PROVIDER.JUSSUB) {
        const result = await submitJussub(values, "CREDIT_CARD");
        await registerCapicandiaWithRetry({
          values,
          subscriptionId: result.subscriptionId,
        });
        if (result.paymentMethod === "PIX") {
          const pix = result.oneTimePayment;
          const cycleDays = result.plan?.cycleDays;
          setPixPaymentData({
            subscriptionId: result.subscriptionId,
            pix_code: pix?.qrCode,
            charge: {
              amount: result.amount * 100,
              charges: [
                {
                  last_transaction: {
                    qr_code: pix?.qrCode,
                    qr_code_url: pix?.qrCodeUrl,
                    additional_information: [],
                  },
                },
              ],
            },
            plan: {
              id: result.plan?.itemId,
              name: result.plan?.name,
              amount: result.plan?.itemAmount,
              type: cycleDays === 365 ? "annually" : "monthly",
            },
          });
          setResult("pix-success");
          setSubmitting(false);
          return;
        }
        setResult("success");
        setSubmitting(false);
        handleCheckoutMarketingData();
        trackSignUpAccountCreated();
        return;
      }

      const cardToken = await tokenize(values);
      const createCard = await saveCard(cardToken.id, values);
      const checkoutValues = buildCheckoutValues({
        values,
        cardHash: values.card_number ? createCard.data.token : "",
        urlParams: buildTrackingUrlParams(locationSearch),
        isPasswordlessCheckout,
      });
      if (checkoutValues.recaptchaVersion === "3") {
        refreshRecaptchaV3Ref.current?.();
      }

      const subscriptionCreate = await subscribe(checkoutValues, createCard.data);
      const pollingSubscriptionId = subscriptionCreate.data.id;

      let pollingResult = isSubscriptionActive(subscriptionCreate.data);
      if (!pollingResult) {
        pollingResult = await pollSubscription(pollingSubscriptionId);
        if (!pollingResult) {
          setResult("error");
          setSubmitting(false);
          return false;
        }
      }

      if (subscriptionCreate.status === 201 || pollingResult) {
        await registerCapicandiaWithRetry({
          values: checkoutValues,
          subscriptionId: pollingSubscriptionId,
        });
        setResult("success");
        setSubmitting(false);
        handleCheckoutMarketingData();
        trackSignUpAccountCreated();
      }
    } catch (error) {
      if (error?.code === "INVALID_RECAPTCHA") return false;
      if (sentryEnabled) {
        captureError({
          tags: { flow: "checkout", provider: "pagarme_v5", trace_id: traceId },
          extras: {
            stage: "onSubmit",
            errors_history: formErrors.slice(-50),
            card_brand: getCardBrand(values.card_number?.replace(/\s/g, "") || ""),
            card_number: maskCardNumber(values.card_number),
          },
          message: error?.message || "General checkout error",
          name: error?.name || "Error",
        });
      }
      trackPaymentFailed(values, {
        error_type: "general_checkout_error",
        error_message: error.message || "Erro durante o processo de checkout",
        error_code: "unknown",
      });
      setResult("error");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const onPix = async () => {
    let checkoutProvider;
    try {
      checkoutProvider = resolveCheckoutProvider({
        isResolved: isJussubResolved,
        isJussubEnabled,
        paymentMethod: "PIX",
      });
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }

    const props = tracking.checkout.prepareCheckoutPixPaymentClickedForAnalytics(
      formik.values, coupom, defaultTrialDays
    );
    trackCheckoutPixPaymentClicked(props);
    try {
      setSubmitting(true);

      if (checkoutProvider === CHECKOUT_PROVIDER.JUSSUB) {
        const result = await submitJussub(formik.values, "PIX");
        await registerCapicandiaWithRetry({
          values: formik.values,
          subscriptionId: result.subscriptionId,
        });
        const pix = result.oneTimePayment;
        const cycleDays = result.plan?.cycleDays;
        setPixPaymentData({
          subscriptionId: result.subscriptionId,
          pix_code: pix?.qrCode,
          charge: {
            amount: result.amount * 100,
            charges: [
              {
                last_transaction: {
                  qr_code: pix?.qrCode,
                  qr_code_url: pix?.qrCodeUrl,
                  additional_information: [],
                },
              },
            ],
          },
          plan: {
            id: result.plan?.itemId,
            name: result.plan?.name,
            amount: result.plan?.itemAmount,
            type: cycleDays === 365 ? "annually" : "monthly",
          },
        });
        setResult("pix-success");
        return;
      }

      const urlParams = buildTrackingUrlParams(locationSearch);
      formik.setFieldValue("urlParams", urlParams);
      formik.setFieldValue(
        "version",
        getCheckoutVersion(isPasswordlessCheckout)
      );
      const checkoutValues = buildCheckoutValues({
        values: formik.values,
        urlParams,
        isPasswordlessCheckout,
      });
      const pixResponse = await createPixSubscription({
        values: checkoutValues,
        coupom: resolveCoupomForPlan(checkoutValues.plan),
        isPasswordlessCheckout,
      });
      await registerCapicandiaWithRetry({
        values: checkoutValues,
        subscriptionId:
          pixResponse.data?.subscription_id || pixResponse.data?.id,
      });
      setPixPaymentData(pixResponse.data);
      setResult("pix-success");
    } catch (error) {
      if (error?.response?.data?.error === "INVALID_RECAPTCHA") {
        handleInvalidRecaptcha(error);
        setPixGenerationFailed(false);
        return;
      }
      setResult("error");
      setPixGenerationFailed(true);
      const failProps = tracking.checkout.prepareCheckoutPixPaymentFailedForAnalytics(
        formik.values, error, coupom, defaultTrialDays
      );
      trackCheckoutPixPaymentFailed(failProps);
      setFailedReason({
        message: "Erro ao continuar com o Pix",
        details: "Por favor, entre em contato com o suporte.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onTryAgain = reset => {
    const props = tracking.checkout.prepareCheckoutTryAgainClickedForAnalytics(
      formik.values, coupom, defaultTrialDays
    );
    trackCheckoutTryAgainClicked(props);
    reset();
  };

  const onAccessPlatform = (origin, reset) => {
    const props = tracking.checkout.prepareCheckoutAccessPlatformClickedForAnalytics(
      formik.values, coupom, defaultTrialDays, origin
    );
    trackCheckoutAccessPlatformClicked(props);
    if (shouldProvisionCapicandia(formik.values.coupom)) {
      const capicandiaUrl = process.env.REACT_APP_CAPICANDIA_URL || "https://capicandia.jusfy.com.br";
      window.location.href = `${capicandiaUrl}/app`;
      return;
    }
    reset();
  };

  return {
    onSubmit,
    onPix,
    onTryAgain,
    onAccessPlatform,
    refreshRecaptchaV3Ref,
  };
}
